import React from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@tempmee/react';
import { RouteLink } from '../../../commonComponents/RouteLink';
import {
  CaptionedLabel,
  TableCardFrame,
  TableCardTitle,
} from '../../../commonComponents/TableCardFrame';
import { locationUserPath } from '../../LocationUsers/paths';

// "Location Count" and "Last Active" are the Location Users list's names for
// the same two values, not the ticket's "Office Count" and "Last Sign in date":
// the count includes locations with no office profile, and auth-service records
// last activity, not last sign-in (GOTU-7483 P1, P2).
const COLUMNS = [
  'Name',
  'Email',
  'Phone Number',
  'Account Status',
  'Location Count',
  'Access',
  'Last Active',
];

/**
 * Who can act for this office (GOTU-7483), as organization-service records it,
 * one row per identity. Rows arrive mapped and sorted; nothing here reformats.
 *
 * The name opens the identity's Location User profile, as it does on the
 * Location Users list — a nameless identity's em dash included, so every row
 * has a way in.
 *
 * "Indirect" access comes from a group or organization grant, and names it
 * when the service does, because removing that access means changing the group
 * or organization rather than this office. There is no remove control: the
 * ticket leaves Unassign out of scope.
 */
export const LocationAdminsTable = ({ admins, isPending, isError }) => (
  <TableCardFrame
    title={<TableCardTitle>Location Admins</TableCardTitle>}
    outline
    columns={COLUMNS}
    rowCount={admins.length}
    isPending={isPending}
    isError={isError}
    emptyMessage="No location admins have access to this office yet."
    errorMessage="Could not load location admins."
  >
    {admins.map((admin) => (
      <TableRow key={admin.id} hover>
        <TableCell>
          <RouteLink to={locationUserPath(admin.id)}>{admin.name}</RouteLink>
        </TableCell>
        <TableCell className="break-all">{admin.email}</TableCell>
        <TableCell>{admin.phoneLabel}</TableCell>
        <TableCell>{admin.statusLabel}</TableCell>
        <TableCell>{admin.locationCountLabel}</TableCell>
        <TableCell>
          <CaptionedLabel label={admin.accessLabel} caption={admin.accessViaLabel} />
        </TableCell>
        <TableCell>{admin.lastActiveLabel}</TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

LocationAdminsTable.propTypes = {
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      phoneLabel: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      locationCountLabel: PropTypes.string.isRequired,
      accessLabel: PropTypes.string.isRequired,
      accessViaLabel: PropTypes.string.isRequired,
      lastActiveLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
};

export default LocationAdminsTable;
